import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabaseClient';

export type MemberRole = 'admin' | 'teacher' | 'student' | 'user';
export type SignupRole = 'teacher' | 'student';
export type MemberStatus = 'pending' | 'approved' | 'rejected';

export interface Profile {
  id: string;
  email: string;
  role: MemberRole;
  status: MemberStatus;
  created_at: string;
  approved_at: string | null;
}

interface AuthContextValue {
  session: Session | null;
  user: User | null;
  profile: Profile | null;
  loading: boolean;
  profileLoading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: string | null }>;
  signUp: (email: string, password: string, role: SignupRole) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function translateAuthError(message: string): string {
  const lower = message.toLowerCase();
  if (lower.includes('invalid login credentials')) return '이메일 또는 비밀번호가 올바르지 않아요.';
  if (lower.includes('email not confirmed')) return '이메일 인증이 아직 완료되지 않았어요. 메일함을 확인해 주세요.';
  if (lower.includes('user already registered') || lower.includes('already been registered'))
    return '이미 가입된 이메일이에요. 로그인해 주세요.';
  if (lower.includes('password should be at least')) return '비밀번호는 6자 이상이어야 해요.';
  if (lower.includes('unable to validate email') || lower.includes('invalid email'))
    return '이메일 형식을 확인해 주세요.';
  if (lower.includes('email rate limit') || lower.includes('over_email_send_rate_limit'))
    return '요청이 잠시 제한됐어요. 1분 정도 뒤에 다시 시도해 주세요.';
  if (lower.includes('security purposes') || lower.includes('you can only request this after'))
    return '보안을 위해 잠시 후 다시 시도해 주세요.';
  if (lower.includes('rate limit') || lower.includes('too many'))
    return '요청이 너무 잦아요. 잠시 후 다시 시도해 주세요.';
  return '요청을 처리하지 못했어요. 잠시 후 다시 시도해 주세요.';
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);

  const loadProfile = useCallback(async (user: User) => {
    setProfileLoading(true);
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .maybeSingle();
      if (error) throw error;

      if (data) {
        setProfile(data as Profile);
        return;
      }

      // 트리거가 생성하지 못한 예외 상황 대비: 최소 권한(대기)으로 프로필 생성
      const { data: created } = await supabase
        .from('profiles')
        .insert({ id: user.id, email: user.email ?? '', role: 'user', status: 'pending' })
        .select('*')
        .maybeSingle();
      setProfile((created as Profile) ?? null);
    } catch {
      setProfile(null);
    } finally {
      setProfileLoading(false);
    }
  }, []);

  useEffect(() => {
    let active = true;

    supabase.auth
      .getSession()
      .then(({ data }) => {
        if (!active) return;
        setSession(data.session);
        setLoading(false);
        if (data.session?.user) void loadProfile(data.session.user);
      })
      .catch(() => {
        if (active) setLoading(false);
      });

    const { data: sub } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession);
      if (nextSession?.user) {
        void loadProfile(nextSession.user);
      } else {
        setProfile(null);
      }
    });

    return () => {
      active = false;
      sub.subscription.unsubscribe();
    };
  }, [loadProfile]);

  const refreshProfile = useCallback(async () => {
    const { data } = await supabase.auth.getSession();
    if (data.session?.user) await loadProfile(data.session.user);
  }, [loadProfile]);

  const signIn = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error: error ? translateAuthError(error.message) : null };
  }, []);

  const signUp = useCallback(async (email: string, password: string, role: SignupRole) => {
    // 인증 메일을 보내지 않고 백엔드에서 계정을 즉시 생성한다.
    const { data, error } = await supabase.functions.invoke('signup-user', {
      body: { email, password, role },
    });

    let code = '';
    if (error) {
      try {
        const res = (error as unknown as { context?: Response }).context;
        if (res && typeof res.json === 'function') {
          const parsed = await res.clone().json();
          code = typeof parsed?.error === 'string' ? parsed.error : '';
        }
      } catch {
        code = '';
      }
    } else if (data && typeof data === 'object') {
      code = typeof (data as { error?: string }).error === 'string' ? String((data as { error?: string }).error) : '';
    }

    if (code) {
      if (code === 'already_registered') return { error: '이미 가입된 이메일이에요. 로그인해 주세요.' };
      if (code === 'invalid_email') return { error: '이메일 형식을 확인해 주세요.' };
      if (code === 'weak_password') return { error: '비밀번호는 6자 이상이어야 해요.' };
      if (code === 'server_not_configured') return { error: '가입 서버 설정이 완료되지 않았어요. 운영자에게 문의해 주세요.' };
      return { error: '가입 신청을 처리하지 못했어요. 잠시 후 다시 시도해 주세요.' };
    }

    // 계정 생성 후 바로 로그인시켜 승인 대기 화면으로 보낸다.
    const { error: signInErr } = await supabase.auth.signInWithPassword({ email, password });
    if (signInErr) return { error: translateAuthError(signInErr.message) };
    return { error: null };
  }, []);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
    setProfile(null);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      user: session?.user ?? null,
      profile,
      loading,
      profileLoading,
      signIn,
      signUp,
      signOut,
      refreshProfile,
    }),
    [session, profile, loading, profileLoading, signIn, signUp, signOut, refreshProfile],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth는 AuthProvider 안에서만 사용할 수 있습니다.');
  return ctx;
}